import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Linkedin, Instagram } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import SEO, { SEO_CONFIGS } from '../components/SEO';
import { Analytics } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [resumeData, setResumeData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    linkedin: '',
    file: null
  });
  const [loading, setLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      Analytics.trackContactSubmission();
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSubmit = async (e) => {
    e.preventDefault();
    
    if (!resumeData.file) {
      toast.error('Please select a resume file');
      return;
    }

    setResumeLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('full_name', resumeData.full_name);
      formDataObj.append('email', resumeData.email);
      formDataObj.append('phone', resumeData.phone);
      formDataObj.append('position', resumeData.position);
      formDataObj.append('experience', resumeData.experience);
      if (resumeData.linkedin) {
        formDataObj.append('linkedin', resumeData.linkedin);
      }
      formDataObj.append('file', resumeData.file);

      await axios.post(`${API}/resumes`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      Analytics.trackResumeSubmission(resumeData.position);
      toast.success('Resume submitted successfully!');
      setResumeData({
        full_name: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        linkedin: '',
        file: null
      });
      // Reset file input
      document.getElementById('resume-file').value = '';
    } catch (error) {
      console.error('Error submitting resume:', error);
      toast.error('Failed to submit resume. Please try again.');
    } finally {
      setResumeLoading(false);
    }
  };

  return (
    <div>
      <SEO {...SEO_CONFIGS.contact} />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-primary text-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl lg:text-6xl font-bold mb-6"
          >
            Get In <span className="gold-text">Touch</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-300 max-w-3xl"
          >
            Whether you're seeking leadership talent or exploring career opportunities, we'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-primary mb-8">Contact Information</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start" data-testid="contact-info-location">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary/10 mr-4">
                    <MapPin size={24} className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Location</h3>
                    <p className="text-muted-foreground">Gurugram, India</p>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-info-email">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary/10 mr-4">
                    <Mail size={24} className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Email</h3>
                    <a href="mailto:connect@vocavi.in" className="text-muted-foreground hover:text-secondary transition-colors">
                      connect@vocavi.in
                    </a>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-info-phone">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary/10 mr-4">
                    <Phone size={24} className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Phone</h3>
                    <a href="tel:+917827644781" className="text-muted-foreground hover:text-secondary transition-colors">
                      +91 7827644781
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-primary mb-4">Connect With Us</h3>
                <div className="flex items-center gap-4">
                  <a 
                    href="https://www.linkedin.com/company/vocavi-partners-llp/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white hover:bg-[#0077B5]/90 transition-colors"
                    data-testid="contact-linkedin"
                  >
                    <Linkedin size={20} />
                    <span>LinkedIn</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/vocavipartners" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white hover:opacity-90 transition-opacity"
                    data-testid="contact-instagram"
                  >
                    <Instagram size={20} />
                    <span>Instagram</span>
                  </a>
                </div>
              </div>

              <div className="bg-muted p-8">
                <h3 className="text-xl font-bold text-primary mb-4">Office Hours</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                  <p>Saturday: By Appointment</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Forms */}
            <div>
              {/* Tabs */}
              <div className="flex gap-4 mb-8" data-testid="contact-tabs">
                <button
                  onClick={() => setActiveTab('contact')}
                  data-testid="tab-contact"
                  className={`flex-1 py-3 px-6 text-sm font-semibold uppercase tracking-wider transition-all ${
                    activeTab === 'contact'
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  Contact Us
                </button>
                <button
                  onClick={() => setActiveTab('resume')}
                  data-testid="tab-resume"
                  className={`flex-1 py-3 px-6 text-sm font-semibold uppercase tracking-wider transition-all ${
                    activeTab === 'resume'
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  Submit Resume
                </button>
              </div>

              {/* Contact Form */}
              {activeTab === 'contact' && (
                <form onSubmit={handleContactSubmit} data-testid="contact-form" className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-primary mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      data-testid="contact-input-name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-primary mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      data-testid="contact-input-email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-primary mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      data-testid="contact-input-phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-primary mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      data-testid="contact-input-company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-primary mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      data-testid="contact-input-message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={loading}
                    className="w-full bg-secondary text-primary hover:bg-secondary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        Send Message <Send className="ml-2" size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Resume Form */}
              {activeTab === 'resume' && (
                <form onSubmit={handleResumeSubmit} data-testid="resume-form" className="space-y-6">
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-medium text-primary mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      data-testid="resume-input-name"
                      required
                      value={resumeData.full_name}
                      onChange={(e) => setResumeData({ ...resumeData, full_name: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="resume_email" className="block text-sm font-medium text-primary mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="resume_email"
                      data-testid="resume-input-email"
                      required
                      value={resumeData.email}
                      onChange={(e) => setResumeData({ ...resumeData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="resume_phone" className="block text-sm font-medium text-primary mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="resume_phone"
                      data-testid="resume-input-phone"
                      required
                      value={resumeData.phone}
                      onChange={(e) => setResumeData({ ...resumeData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-primary mb-2">
                      Position Sought *
                    </label>
                    <input
                      type="text"
                      id="position"
                      data-testid="resume-input-position"
                      required
                      value={resumeData.position}
                      onChange={(e) => setResumeData({ ...resumeData, position: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="experience" className="block text-sm font-medium text-primary mb-2">
                      Years of Experience *
                    </label>
                    <input
                      type="text"
                      id="experience"
                      data-testid="resume-input-experience"
                      required
                      value={resumeData.experience}
                      onChange={(e) => setResumeData({ ...resumeData, experience: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="linkedin" className="block text-sm font-medium text-primary mb-2">
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      id="linkedin"
                      data-testid="resume-input-linkedin"
                      value={resumeData.linkedin}
                      onChange={(e) => setResumeData({ ...resumeData, linkedin: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>

                  <div>
                    <label htmlFor="resume-file" className="block text-sm font-medium text-primary mb-2">
                      Resume (PDF or DOCX) *
                    </label>
                    <input
                      type="file"
                      id="resume-file"
                      data-testid="resume-input-file"
                      required
                      accept=".pdf,.docx"
                      onChange={(e) => setResumeData({ ...resumeData, file: e.target.files[0] })}
                      className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="resume-submit-button"
                    disabled={resumeLoading}
                    className="w-full bg-secondary text-primary hover:bg-secondary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resumeLoading ? 'Submitting...' : (
                      <>
                        Submit Resume <Send className="ml-2" size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;